import React, { useState, useEffect } from 'react';

interface AuthorizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  fallbackSrc?: string;
  loadingComponent?: React.ReactNode;
}

const AuthorizedImage: React.FC<AuthorizedImageProps> = ({
  src,
  fallbackSrc = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EImage not found%3C/text%3E%3C/svg%3E',
  loadingComponent,
  alt,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    let objectUrl: string | null = null;

    const fetchImage = async () => {
      if (!src) {
        setHasError(true);
        setIsLoading(false);
        return;
      }

      // If it's a data URL or blob URL, use it directly
      if (src.startsWith('data:') || src.startsWith('blob:')) {
        setImageSrc(src);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setHasError(false);

        const token = localStorage.getItem('access_token');
        const headers: HeadersInit = token ? { 'Authorization': `Bearer ${token}` } : {};

        const response = await fetch(src, { headers });

        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.statusText}`);
        }

        const blob = await response.blob();
        if (isMounted) {
          objectUrl = URL.createObjectURL(blob);
          setImageSrc(objectUrl);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading authorized image:', error);
        if (isMounted) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  if (isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }
    // Return a simple placeholder or null to avoid src="" warning
    // Using a div with same className to maintain layout if possible
    return <div className={`bg-gray-200 animate-pulse ${props.className || ''}`} style={props.style || { width: '100%', height: '100%' }} />;
  }

  if (hasError) {
    return (
      <img
        src={fallbackSrc}
        alt={alt || 'Fallback image'}
        {...props}
      />
    );
  }

  if (!imageSrc) {
    return null; // Or render a skeleton/placeholder if desired, but avoid src=""
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      {...props}
    />
  );
};

export default AuthorizedImage;
